import {
    Alert,
    Box,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    Stack,
    SvgIcon,
    Typography,
    Dialog,
    DialogContent,
    ListItemButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Outlet } from 'react-router-dom';

import mainLogo from 'assets/images/logo_blanco.png';
import LogoutIcon from 'assets/images/icons/logout.png';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MuiTypography from '@mui/material/Typography';
import { MENU_OPEN, SET_AUTHENTICATED, SET_TOKEN, SET_USER_DATA } from 'store/actions';
import { ACCESS_TOKEN, AUTH, USER_SESSION } from '../../assets/constants';
import { clearAssignedMachine } from '../../services/operatorSessionService';
import { useSupervisorNotifications } from '../../hooks/useWebSocket';
import NotificationsIcon from '@mui/icons-material/Notifications';
import React, { useEffect, useRef, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import { syncQueue } from '../../services/helpers/offlineHelpers';
import apiClient from '../../services/apiClient';
import { preloadCatalogs, preloadOperatorRoutes } from '../../utils/preload';
import userService from '../../services/UserService';

export function ActionItem({ label, icon: Icon, to, onClick, color }) {
    const isLink = Boolean(to);

    return (
        <Box
            component={isLink ? RouterLink : 'button'}
            to={isLink ? to : undefined}
            onClick={onClick}
            aria-label={label}
            sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'row', // 👈 CAMBIO AQUÍ
                alignItems: 'center', // 👈 centra verticalmente
                gap: 1, // 👈 espacio entre icono y texto
                minWidth: 64,
                textDecoration: 'none',
                color: color ?? 'inherit',

                '&:focus-visible': {
                    outline: '2px solid',
                    outlineOffset: 2
                }
            }}
        >
            <img src={Icon} alt={label} style={{ width: 15, height: 15, color: color ?? 'inherit' }} />
            <Typography variant="subtitle2" component="span" color={color ?? 'black'} align={'center'}>
                {label}
            </Typography>
        </Box>
    );
}

export default function OperatorHeader() {
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const dispatch = useDispatch();
    const nav = useNavigate();
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    const isAuthenticated = customization.autenticated;
    const userId = customization.userData?.id;
    const userRole = customization.userData?.roles?.[0]?.systemName;

    const messages = useSupervisorNotifications(isAuthenticated, userRole);

    const [unreadCount, setUnreadCount] = useState(0);
    const { showAlert } = useAlert();
    const [openDialog, setOpenDialog] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loadingNotifications, setLoadingNotifications] = useState(false);

    useEffect(() => {
        if (navigator.onLine) {
            preloadOperatorRoutes();
            preloadCatalogs(userId);
        }
    }, []);

    // Cada vez que llega un mensaje, aumentamos el dot y mostramos alert
    useEffect(() => {
        if (messages.length > 0) {
            setUnreadCount(messages.length);
            const lastMessage = messages[messages.length - 1];
            showAlert(lastMessage, 'warning');
        }
    }, [messages]);

    const handleLogOut = () => {
        dispatch({ type: SET_TOKEN, token: '' });
        dispatch({ type: SET_USER_DATA, userData: {} });
        dispatch({ type: SET_AUTHENTICATED, autenticated: false });
        dispatch({ type: MENU_OPEN, id: 'default' });
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(USER_SESSION);
        localStorage.setItem(AUTH, '0');
        clearAssignedMachine();
        nav('/');
    };

    // Limpiar el dot al entrar a la ruta de notificaciones
    useEffect(() => {
        if (location.pathname === '/supervisor/edition-requests') {
            setUnreadCount(0);
        }
    }, [location.pathname]);

    const handleClick = async () => {
        setUnreadCount(0);
        setOpenDialog(true);

        try {
            setLoadingNotifications(true);

            const res = await userService.getNotifications();
            setNotifications(res.data || []);
        } catch (error) {
            showAlert('Error al cargar notificaciones', 'error');
        } finally {
            setLoadingNotifications(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const prevOnlineRef = useRef(navigator.onLine);

    useEffect(() => {
        const wasOnline = prevOnlineRef.current;

        // 🔥 SOLO cuando pasa de offline → online
        if (!wasOnline && isOnline && userRole === 'OPERATOR') {
            showAlert('Conexión restaurada', 'success');

            syncQueue({
                apiClient,
                showAlert
            });
        }

        // actualizar estado anterior
        prevOnlineRef.current = isOnline;
    }, [isOnline]);

    const formatRelativeDate = (date) => {
        if (!date) return '';

        // Normalizar milisegundos (por tu formato .53029)
        const safeDate = date.replace(/(\.\d{3})\d+/, '$1');

        const d = new Date(safeDate);
        const now = new Date();

        const diffMs = now - d;
        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffSeconds < 60) {
            return 'Hace unos segundos';
        }

        if (diffMinutes < 60) {
            return `Hace ${diffMinutes} minuto${diffMinutes === 1 ? '' : 's'}`;
        }

        if (diffHours < 24) {
            return `Hace ${diffHours} hora${diffHours === 1 ? '' : 's'}`;
        }

        if (diffDays === 1) {
            return 'Ayer';
        }

        if (diffDays < 7) {
            return `Hace ${diffDays} día${diffDays === 1 ? '' : 's'}`;
        }

        // ya más viejo → fecha normal
        return d.toLocaleDateString('es-MX');
    };

    return (
        <>
            <Box
                component="header"
                role="banner"
                sx={{
                    display: 'flex',
                    width: '100%',
                    height: { xs: '10vh' },
                    overflow: 'hidden',
                    backgroundColor: theme.palette.primary.main,
                    px: 4
                }}
            >
                <Box
                    sx={{
                        flex: { xs: '0 0 50%' },
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'left'
                    }}
                >
                    <Box component={'img'} src={mainLogo} alt="logo" sx={{ height: { xs: '5vh' } }} />
                    <Stack
                        direction="column"
                        sx={{
                            display: 'flex',
                            gap: { md: 0 },
                            pointerEvents: 'auto',
                            p: 1
                        }}
                    >
                        <MuiTypography color={'white'} variant={'subtitle1'}>
                            HR x HR Digital Boards
                        </MuiTypography>
                        <MuiTypography color={'white'} variant={'subtitle2'}>
                            Sistema de producción
                        </MuiTypography>
                    </Stack>
                </Box>

                <Box
                    component="nav"
                    aria-label="Acciones principales"
                    sx={{
                        flex: { xs: '0 0 50%' },
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'right'
                    }}
                    gap={2}
                >
                    {isAuthenticated && (
                        <>
                            {!isOnline && (
                                <Alert severity={'warning'} variant="filled" sx={{ py: 2 }}>
                                    Modo fuera de línea
                                </Alert>
                            )}

                            {userRole === 'SUPERVISOR' && (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                        mr: 2
                                    }}
                                    onClick={handleClick}
                                >
                                    <NotificationsIcon style={{ color: 'white' }} />

                                    {unreadCount > 0 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                backgroundColor: 'red'
                                            }}
                                        />
                                    )}
                                </Box>
                            )}

                            <Stack
                                direction="column"
                                sx={{
                                    display: 'flex',
                                    gap: { md: 0 },
                                    pointerEvents: 'auto'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: {
                                            xs: 'none',
                                            sm: 'none',
                                            md: 'flex'
                                        },
                                        flexDirection: 'column',
                                        justifyContent: 'center', // 👈 pegado a la derecha
                                        alignItems: 'left'
                                    }}
                                >
                                    <MuiTypography color={'white'} variant={'subtitle1'}>
                                        {customization.userData.fullName || 'Desconocido'}
                                    </MuiTypography>
                                </Box>
                                <ActionItem
                                    label="Cerrar sesión"
                                    icon={LogoutIcon}
                                    color={theme.palette.secondary.dark}
                                    onClick={handleLogOut}
                                />
                            </Stack>
                        </>
                    )}
                </Box>
            </Box>
            <Outlet />
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
                <DialogTitle>Notificaciones</DialogTitle>

                <DialogContent dividers>
                    {loadingNotifications ? (
                        <Typography>Cargando...</Typography>
                    ) : notifications.length === 0 ? (
                        <Typography>No tienes notificaciones para el turno actual</Typography>
                    ) : (
                        <List>
                            {notifications.map((notif, index) => {
                                const isEditable = notif.type === 'EDIT_REQUEST';

                                const content = (
                                    <ListItemText
                                        primary={notif.title || notif}
                                        secondary={
                                            <>
                                                <Typography variant="body2">{notif.description}</Typography>

                                                <Typography variant="caption" color="text.secondary">
                                                    {formatRelativeDate(notif.notifyAt)}
                                                </Typography>
                                            </>
                                        }
                                    />
                                );

                                return isEditable ? (
                                    <ListItem key={index} divider disablePadding>
                                        <ListItemButton
                                            onClick={() => {
                                                setOpenDialog(false); // 🔥 cerrar dialog
                                                nav('/supervisor/edition-requests');
                                            }}
                                        >
                                            {content}
                                        </ListItemButton>
                                    </ListItem>
                                ) : (
                                    <ListItem key={index} divider>
                                        {content}
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
